import type { CreatePackageOrderInputDto } from "@/types/codegen";

export enum PurchaseStatus {
  SUCCESSFUL = "SUCCESSFUL",
  CANCELLED = "CANCELLED",
  FAILED = "FAILED",
  EXPIRED = "EXPIRED",
}

export type PaymentResult = {
  orderId: string;
  status: PurchaseStatus;
};

export type PaymentMethod = "qiAccount" | "checkout" | "bnpl";
export type PaymentProviderType = "standard" | "bnpl";

type PaymentProviderBase<TParams> = {
  initiatePayment: (params: TParams) => Promise<PaymentResult>;
};

export type TripPaymentParams = Omit<CreatePackageOrderInputDto, "provider">;

type SuccessCallbackData = {
  orderId: string;
};

type FailureCallbackData = {
  orderId: string | undefined;
  errorType: string;
  errorMessage: string;
};

export type Callbacks = {
  onSuccess: (data: SuccessCallbackData) => void;
  onFailure: (data: FailureCallbackData) => void;
};

export type StandardTripPaymentParams = TripPaymentParams &
  Callbacks & {
    serviceType: "trip";
  };

export type StandardFlightPaymentParams = Callbacks & {
  availabilityTokens: string[];
  serviceType: "air";
};

export type StandardHotelPaymentParams = Callbacks & {
  availabilityTokens: string[];
  serviceType: "hotel";
};

export type StandardInitiatePaymentParams =
  | StandardTripPaymentParams
  | StandardFlightPaymentParams
  | StandardHotelPaymentParams;

export const isTripPayment = (
  params: StandardInitiatePaymentParams,
): params is StandardTripPaymentParams => {
  return params.serviceType === "trip";
};

export type StandardPaymentProvider =
  PaymentProviderBase<StandardInitiatePaymentParams>;

export type BnplPaymentProvider = PaymentProviderBase<{
  availabilityTokens: string[];
  sessionId: string;
  otp: string;
}>;

export type PaymentProvider = StandardPaymentProvider | BnplPaymentProvider;
